import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { fetchShopifyOrders, shopifyOrderAdminUrl, type ShopifyOrder } from '@/lib/shopifyAdmin';
import { ExternalLink, Calendar, Package, FlaskConical, Sparkles } from 'lucide-react';

type Appt = {
  id: string;
  starts_at: string;
  status: string;
  treatments: any;
  client_name: string | null;
};

type RxSubmission = {
  id: string;
  name: string;
  email: string;
  status: string;
  created_at: string;
};

const APPT_STATUS_LABELS: Record<string, string> = {
  booked: 'מאושר',
  completed: 'הושלם',
  noshow: 'לא הגיע/ה',
  cancelled: 'בוטל',
};

const RX_STATUS_LABELS: Record<string, string> = {
  new: 'חדש',
  in_review: 'בבדיקה',
  contacted: 'יצרנו קשר',
  done: 'טופל',
};

export default function AdminDashboard() {
  const [orders, setOrders] = useState<ShopifyOrder[]>([]);
  const [appts, setAppts] = useState<Appt[]>([]);
  const [rx, setRx] = useState<RxSubmission[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const nowIso = new Date().toISOString();
        const [ordersData, apptsRes, rxRes] = await Promise.all([
          fetchShopifyOrders('fulfillment_status:unshipped OR fulfillment_status:partial').catch(
            () => [] as ShopifyOrder[],
          ),
          supabase
            .from('appointments')
            .select('id,starts_at,status,treatments,client_name')
            .gte('starts_at', nowIso)
            .neq('status', 'cancelled')
            .order('starts_at', { ascending: true })
            .limit(20),
          supabase
            .from('personal_rx_submissions')
            .select('id,name,email,status,created_at')
            .in('status', ['new', 'in_review', 'contacted'])
            .order('created_at', { ascending: false })
            .limit(20),
        ]);
        if (!mounted) return;
        setOrders(ordersData);
        setAppts(((apptsRes.data as any) ?? []) as Appt[]);
        setRx(((rxRes.data as any) ?? []) as RxSubmission[]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">לוח בקרה</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Column
          title="הזמנות מוצרים פעילות"
          icon={Package}
          to="/admin/orders"
          count={orders.length}
          loading={loading}
          emptyText="אין הזמנות פתוחות."
        >
          {orders.slice(0, 8).map((o) => (
            <a
              key={o.id}
              href={shopifyOrderAdminUrl(o.id)}
              target="_blank"
              rel="noreferrer"
              className="block p-3 hover:bg-neutral-50 border-b last:border-b-0"
            >
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm flex items-center gap-1">
                    {o.name}
                    <ExternalLink className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {o.customer?.displayName ?? o.email ?? '—'}
                  </div>
                </div>
                <div className="text-xs text-left shrink-0">
                  <div className="tabular-nums font-medium">
                    {parseFloat(o.totalPriceSet.shopMoney.amount).toLocaleString('he-IL', {
                      style: 'currency',
                      currency: o.totalPriceSet.shopMoney.currencyCode,
                    })}
                  </div>
                  <div className="text-muted-foreground">
                    {new Date(o.createdAt).toLocaleDateString('he-IL')}
                  </div>
                </div>
              </div>
            </a>
          ))}
        </Column>

        <Column
          title="הזמנות שירותים קרובות"
          icon={Sparkles}
          to="/admin/appointments"
          count={appts.length}
          loading={loading}
          emptyText="אין תורים קרובים."
        >
          {appts.slice(0, 8).map((a) => (
            <Link
              key={a.id}
              to="/admin/appointments"
              className="block p-3 hover:bg-neutral-50 border-b last:border-b-0"
            >
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{a.client_name ?? '—'}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {Array.isArray(a.treatments) ? a.treatments.join(', ') : ''}
                  </div>
                </div>
                <div className="text-xs text-left shrink-0">
                  <div className="font-medium">
                    {new Date(a.starts_at).toLocaleDateString('he-IL')}
                  </div>
                  <div className="text-muted-foreground tabular-nums">
                    {new Date(a.starts_at).toLocaleTimeString('he-IL', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>
              </div>
              <Badge variant="outline" className="mt-1 text-[10px]">
                {APPT_STATUS_LABELS[a.status] ?? a.status}
              </Badge>
            </Link>
          ))}
        </Column>

        <Column
          title="פניות רוקחות אישית"
          icon={FlaskConical}
          to="/admin/personal-rx"
          count={rx.length}
          loading={loading}
          emptyText="אין פניות פתוחות."
        >
          {rx.slice(0, 8).map((r) => (
            <Link
              key={r.id}
              to="/admin/personal-rx"
              className="block p-3 hover:bg-neutral-50 border-b last:border-b-0"
            >
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0">
                  {new Date(r.created_at).toLocaleDateString('he-IL')}
                </div>
              </div>
              <Badge variant="outline" className="mt-1 text-[10px]">
                {RX_STATUS_LABELS[r.status] ?? r.status}
              </Badge>
            </Link>
          ))}
        </Column>
      </div>
    </div>
  );
}

function Column({
  title,
  icon: Icon,
  to,
  count,
  loading,
  emptyText,
  children,
}: {
  title: string;
  icon: any;
  to: string;
  count: number;
  loading: boolean;
  emptyText: string;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-0 overflow-hidden flex flex-col">
      <Link
        to={to}
        className="flex items-center justify-between p-4 border-b hover:bg-neutral-50"
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-medium">{title}</h2>
        </div>
        <Badge variant="secondary">{loading ? '…' : count}</Badge>
      </Link>
      <div className="flex-1 max-h-[500px] overflow-y-auto">
        {loading ? (
          <div className="p-6 text-sm text-muted-foreground text-center">טוען…</div>
        ) : count === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">{emptyText}</div>
        ) : (
          children
        )}
      </div>
    </Card>
  );
}
